import { useEffect } from 'react'

import { env } from '@/shared/config/env'

/**
 * Names the page in the tab, the history and the bookmark bar.
 *
 * Every screen in this panel used to be «Admin». Five open tabs looked
 * identical, browser history was a column of the same word, and a screen reader
 * announced the same string after every navigation — which is WCAG 2.4.2, and
 * a plain nuisance for everyone else.
 *
 * It is a hook rather than a route option because the title is a translated
 * string, and on a record page it is the record's own name: both are known at
 * render, not at route definition. Call it beside the `PageHeader` that already
 * has the words.
 *
 * `undefined` while a record is still loading leaves the previous title alone
 * rather than flashing the bare product name.
 */
export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (title === undefined || title === '') return

    const previous = document.title
    document.title = `${title} · ${env.VITE_APP_NAME}`

    // Restored on unmount so a page that leaves before the next one has
    // rendered does not hand its own name to whatever comes after it.
    return () => {
      document.title = previous
    }
  }, [title])
}
